// FILE: src/services/dashboardLoadTiming.js
//
// Load-timing instrumentation for admin dashboards (added 2026-09-16 after an
// intermittent ~30 s Telemetry load that could not be reproduced on demand).
//
// Splits a page load into:
//   • per-request CLIENT time — wall clock from fetch start to promise settle
//     (browser queueing + network + PostgREST + database)
//   • DATA time  — first request start → last request settled
//   • RENDER time — data settled → next animation frame after React commit
//   • SERVER time (optional) — database execution per aggregate, from the
//     admin-only telemetry_dashboard_server_timings RPC. Client − server for the
//     same aggregate ≈ network + API overhead.
//
// Everything here is pure or side-effect-free apart from the clock, so it is
// unit-tested without a browser.

const now = () => (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now());

/** A load threshold above which we auto-capture server timings and log an event. */
export const SLOW_DASHBOARD_LOAD_MS = 3000;

/**
 * Create a recorder for one page load.
 * `track(label, promise)` returns the promise's value untouched and records timing,
 * including for rejections (which are re-thrown).
 */
export function createLoadRecorder() {
  const startedAt = now();
  const requests = [];
  let dataSettledAt = null;
  let renderedAt = null;

  async function track(label, promiseOrFactory) {
    const t0 = now();
    const entry = { label, startMs: Math.round(t0 - startedAt), ms: null, ok: true };
    requests.push(entry);
    try {
      const p = typeof promiseOrFactory === 'function' ? promiseOrFactory() : promiseOrFactory;
      return await p;
    } catch (err) {
      entry.ok = false;
      throw err;
    } finally {
      entry.ms = Math.round(now() - t0);
    }
  }

  return {
    track,
    markDataSettled() { dataSettledAt = now(); },
    markRendered() { renderedAt = now(); },
    summary() { return summarizeLoad({ startedAt, dataSettledAt, renderedAt, requests }); },
  };
}

/** Pure: shape raw marks into a report. */
export function summarizeLoad({ startedAt, dataSettledAt, renderedAt, requests }) {
  const reqs = (requests || []).map((r) => ({ ...r }));
  const dataMs = dataSettledAt == null ? null : Math.round(dataSettledAt - startedAt);
  const renderMs = dataSettledAt == null || renderedAt == null ? null : Math.round(renderedAt - dataSettledAt);
  const totalMs = renderedAt == null ? dataMs : Math.round(renderedAt - startedAt);
  const slowest = reqs.filter((r) => r.ms != null).sort((a, b) => b.ms - a.ms)[0] || null;
  // Requests launched together should all start within a few ms; a large
  // spread means they were serialized somewhere (browser connection limits,
  // an awaited call, etc.).
  const starts = reqs.map((r) => r.startMs);
  const startSpreadMs = starts.length ? Math.max(...starts) - Math.min(...starts) : 0;
  return { totalMs, dataMs, renderMs, requestCount: reqs.length, startSpreadMs, slowest, requests: reqs };
}

/**
 * Pure: merge client request timings with server execution timings.
 * serverTimings: { [label]: ms } from telemetry_dashboard_server_timings.
 */
export function mergeServerTimings(summary, serverTimings) {
  if (!summary) return summary;
  const st = serverTimings || {};
  const requests = summary.requests.map((r) => {
    const serverMs = Number.isFinite(Number(st[r.label])) ? Number(st[r.label]) : null;
    return { ...r, serverMs, overheadMs: serverMs == null || r.ms == null ? null : Math.max(0, r.ms - serverMs) };
  });
  const withServer = requests.filter((r) => r.serverMs != null);
  const serverTotalMs = withServer.length ? withServer.reduce((s, r) => s + r.serverMs, 0) : null;
  const maxServerMs = withServer.length ? Math.max(...withServer.map((r) => r.serverMs)) : null;
  return { ...summary, requests, serverTotalMs, maxServerMs };
}

/**
 * Pure: a one-line diagnosis of where the time went.
 * Server timings are captured AFTER the load, so they describe the database
 * now, not necessarily during the slow load — the wording says so.
 */
export function diagnoseLoad(report) {
  if (!report || report.totalMs == null) return null;
  const { dataMs, renderMs, maxServerMs, slowest } = report;
  if (renderMs != null && dataMs != null && renderMs > dataMs) return 'render';
  if (maxServerMs != null && slowest && slowest.ms != null) {
    return maxServerMs >= slowest.ms * 0.5 ? 'database' : 'network_or_api';
  }
  return 'data';
}
